import React, { useState, useEffect } from 'react';
import { purchaseService } from '../../utils/apiService';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  TextField,
  IconButton,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { CONTROL_STYLE } from '../../constants/invoiceConstants';
import image from '../../assets/riyal.png'

/**
 * Format currency for display
 */
const formatCurrency = (amount, currencyIcon) => {
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
  
  const iconToUse = currencyIcon || image;
  
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <img src={iconToUse} alt="currency" style={{ width: '13px', height: '13px', objectFit: 'contain' }} />
      <span>{formatted}</span>
    </Box>
  );
};

/**
 * Format date for display
 */
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

/**
 * Get color for payment status
 */
const getStatusColor = (status) => {
  const lowerStatus = (status || '').toLowerCase();
  switch (lowerStatus) {
    case 'paid':
    case 'completed':
      return 'success';
    case 'partial':
      return 'warning';
    case 'pending':
    case 'unpaid':
      return 'error';
    default:
      return 'default';
  }
};

export const SelectPurchaseInvoiceDialog = ({ open, onClose, onSelectInvoice, currencyIcon }) => {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setLoading(true);
        const response = await purchaseService.getPurchases();
        const data = response?.purchases || response || [];
        setInvoices(Array.isArray(data) ? data : []);
        setError(null);
      } catch (err) {
        setError('Failed to fetch purchase invoices');
        console.error('Error fetching purchase invoices:', err);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchInvoices();
      setSearchQuery('');
      setPage(0);
    }
  }, [open]);

  const filteredInvoices = invoices.filter(invoice => {
    const query = searchQuery.toLowerCase();
    return (
      (invoice.purchaseNumber || '').toLowerCase().includes(query) ||
      (invoice.supplierId?.name || invoice.supplier?.name || '').toLowerCase().includes(query)
    );
  });

  const paginatedInvoices = filteredInvoices.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        p: 2
      }}>
        <Typography variant="h6" fontWeight={600}>
          Select Purchase Invoice for Return
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 1.5
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <TextField
                placeholder="Search by purchase number or supplier name"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                fullWidth
                size="small"
                sx={{
                  ...CONTROL_STYLE,
                  mt: 1.5,
                  '& .MuiInputBase-root': {
                    height: '32px'
                  }
                }}
              />
              <Button
                variant="contained"
                color="primary"
                sx={{
                  mt: 1.5,
                  ml: 1,
                  height: '32px',
                  minWidth: 'auto',
                  borderRadius: 0,
                  px: 2,
                  pointerEvents: 'none' // Since search is instant, this is mostly visual
                }}
                startIcon={<SearchIcon fontSize="small" />}
              >
                Search
              </Button>
            </Box>
          </Box>
        </Box>

        <TableContainer component={Paper} sx={{ 
          boxShadow: 'none', 
          border: '1px solid rgba(0,0,0,0.12)',
          borderRadius: '0px',
          mb: 2
        }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ 
                backgroundColor: '#f5f5f5',
                '& th': {
                  py: 1,
                  fontSize: '0.75rem',
                  fontWeight: 700
                }
              }}>
                <TableCell>Purchase #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography color="error" variant="body2">{error}</Typography>
                  </TableCell>
                </TableRow>
              ) : filteredInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No purchase invoices found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedInvoices.map((invoice) => (
                  <TableRow 
                    key={invoice._id || invoice.id}
                    hover
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        cursor: 'pointer'
                      },
                      '& td': {
                        py: 0.75
                      }
                    }}
                    onClick={() => onSelectInvoice(invoice)}
                  >
                    <TableCell>{invoice.purchaseNumber}</TableCell>
                    <TableCell>{formatDate(invoice.issueDate)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                        {invoice.supplierId?.name || invoice.supplier?.name || 'Walk-in Supplier'}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatCurrency(invoice.totalAmount, currencyIcon)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={invoice.paymentStatus || invoice.status || 'N/A'} 
                        size="small"
                        color={getStatusColor(invoice.paymentStatus || invoice.status)}
                        sx={{ 
                          height: 24,
                          textTransform: 'capitalize',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectInvoice(invoice);
                        }}
                        sx={{ 
                          borderRadius: 0,
                          py: 0.25,
                          px: 1,
                          minWidth: 'auto',
                          textTransform: 'none',
                          fontSize: '0.75rem'
                        }}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredInvoices.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          sx={{
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              fontSize: '0.875rem',
            },
            '.MuiTablePagination-select': {
              ...CONTROL_STYLE
            },
            '.MuiToolbar-root': {
              minHeight: '40px',
              height: '40px',
              paddingTop: 0,
              paddingBottom: 0
            }
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default SelectPurchaseInvoiceDialog;
